'use client';
import { gql } from '@apollo/client';
import React from 'react';
import { Else, If, Then } from 'react-if';

import { Box } from '@nerdwallet/currency/components/Box';
import { Column } from '@nerdwallet/currency/components/Column';
import { Columns } from '@nerdwallet/currency/components/Columns';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Text } from '@nerdwallet/currency/components/Text';
import { Tooltip } from '@nerdwallet/currency/components/Tooltip';

import { UserInsightEligibleModulesEnum } from '@generated-gql/generated-types';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';

import AppDownloadFooter from '../app-download-footer';
import styles from './index.module.scss';
import UserInsightCard from './user-insight-card';

const DEFAULT_MAX_CARDS = 3;

interface Props {
  analyticsPayload: {
    moduleName: string;
    sectionPosition: number;
  };
  eligibleModule: UserInsightEligibleModulesEnum;
  title: string;
  tooltip: string;
}

export const QUERY = gql`
  query GetCreditScoreInsights(
    $eligibleModule: UserInsightEligibleModulesEnum!
  ) {
    userInsights(filter: { eligibleModules: [$eligibleModule] }) {
      userInsights {
        id
        ...creditScoreUserInsightCardFragment
      }
    }
  }
  ${UserInsightCard.fragment}
`;

const Insights: React.FC<Props> = ({
  analyticsPayload,
  eligibleModule,
  title,
  tooltip,
}) => {
  const logger = getLogger(`Insights_${eligibleModule}`);
  const { data, loading } = useQueryWithLog(logger, QUERY, {
    variables: {
      eligibleModule,
    },
  });
  const userInsights = data?.userInsights?.userInsights || [];

  if (!userInsights.length) {
    return null;
  }

  return (
    <>
      <Stack spacing="4">
        <Box display="flex" justifyContent="space-between">
          <Box display="flex">
            <Text bold size="large">
              {title}
            </Text>
            <Tooltip aria-label={title} content={tooltip} placement="bottom" />
          </Box>
        </Box>
        <If condition={loading}>
          <Then>
            <LoadingSpinner />
          </Then>
          <Else>
            <Box
              backgroundColor={{
                desktop: 'yellow-lightest',
              }}
              className={styles.container}
              paddingX={{
                desktop: 3,
                mobile: 0,
              }}
              paddingY={{ desktop: 3, mobile: 0 }}
            >
              <Columns collapse>
                {userInsights
                  .slice(0, DEFAULT_MAX_CARDS)
                  .map((userInsight, i) => (
                    <Column equalHeight key={userInsight.id} width="4">
                      <UserInsightCard
                        analyticsPayload={{
                          ...analyticsPayload,
                          elementPosition: i,
                        }}
                        userInsight={userInsight}
                      />
                    </Column>
                  ))}
              </Columns>
            </Box>
          </Else>
        </If>
      </Stack>
      <AppDownloadFooter />
    </>
  );
};

export default Insights;
